/**
 * Affiliate — public landing page for the affiliate programme.
 *
 * Deliberately outside `AppShell`: it is a marketing page with its own header
 * and footer, reachable by anyone, and it must not present the member sidebar.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BrandLogo } from '../components/ui/BrandLogo';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import {
  AffiliateCheckBulletIcon,
  AffiliateEarningsBadgeIcon,
  AffiliateGrowthIcon,
  AffiliateHeartIcon,
  AffiliateMegaphoneIcon,
  AffiliateRatingBadgeIcon,
  AffiliateRobiIcon,
} from '../features/affiliate/affiliateIcons';
import socialProofImage from '../assets/images/affiliate/affiliate-hero__social-proof.webp';
import heroPhoneImage from '../assets/images/affiliate/hero-content__image.webp';
import heroRobiImage from '../assets/images/affiliate/robi-ai-hero.webp';
import robiImage from '../assets/images/affiliate/robi-ai.webp';
import stepOneImage from '../assets/images/affiliate/process-card-img-1.webp';
import stepTwoImage from '../assets/images/affiliate/process-card-img-2.webp';
import stepThreeImage from '../assets/images/affiliate/process-card-img-3.webp';
import stepFourImage from '../assets/images/affiliate/process-card-img-4.webp';
import '../features/affiliate/affiliateLanding.css';

const VALUE_CARDS = [
  { key: 'accountability', Icon: AffiliateHeartIcon },
  { key: 'robi', Icon: AffiliateRobiIcon },
  { key: 'progress', Icon: AffiliateGrowthIcon },
] as const;

const BENEFIT_CARDS = [
  { key: 'improveLives', Icon: AffiliateRobiIcon },
  { key: 'meaningfulProduct', Icon: AffiliateMegaphoneIcon },
  { key: 'growTogether', Icon: AffiliateGrowthIcon },
  { key: 'dedicatedSupport', Icon: AffiliateHeartIcon },
] as const;

const PROCESS_STEPS = [
  { key: 'apply', image: stepOneImage },
  { key: 'approved', image: stepTwoImage },
  { key: 'dashboard', image: stepThreeImage },
  { key: 'share', image: stepFourImage },
] as const;

export function Affiliate() {
  const { t } = useTranslation();
  const robiBullets = t('affiliate.aiBuddy.bullets', {
    returnObjects: true,
  }) as string[];

  return (
    <div className="affiliate-landing">
      {/* Establishes the positioning context the absolute header and the hero
          collage overhang are measured against. */}
      <div className="affiliate-landing__page">
        <header className="affiliate-landing__header">
          <div className="affiliate-landing__wrapper">
            <div className="affiliate-landing__header-inner">
              <Link
                className="affiliate-landing__brand"
                to={AFFILIATE_ROUTES.LANDING}
              >
                <BrandLogo />
              </Link>
              <nav
                className="affiliate-landing__nav"
                aria-label={t('affiliate.accessibility.landingNav')}
              >
                <Link className="affiliate-btn" to={AFFILIATE_ROUTES.SIGN_IN}>
                  {t('affiliate.nav.login')}
                </Link>
                <Link
                  className="affiliate-btn affiliate-btn--primary"
                  to={AFFILIATE_ROUTES.SIGN_UP}
                >
                  {t('affiliate.nav.join')}
                </Link>
              </nav>
            </div>
          </div>
        </header>

        <main>
          <section className="affiliate-landing__section affiliate-hero">
            <div className="affiliate-landing__wrapper">
              <div className="affiliate-hero__inner">
                <div className="affiliate-hero__content">
                  <div className="affiliate-hero__title">
                    <span className="affiliate-hero__eyebrow">
                      {t('affiliate.hero.eyebrow')}
                    </span>
                    <h1>{t('affiliate.hero.title')}</h1>
                  </div>
                  <p className="affiliate-hero__description">
                    {t('affiliate.hero.description')}
                  </p>
                  <div className="affiliate-hero__actions">
                    <Link
                      className="affiliate-btn affiliate-btn--primary"
                      to={AFFILIATE_ROUTES.SIGN_UP}
                    >
                      {t('affiliate.nav.join')}
                    </Link>
                    <Link className="affiliate-btn" to={AFFILIATE_ROUTES.SIGN_IN}>
                      {t('affiliate.nav.loginPrompt')}
                    </Link>
                  </div>
                  <p className="affiliate-hero__social-proof">
                    <img src={socialProofImage} alt="" aria-hidden />
                    <span>
                      <strong>{t('affiliate.hero.socialProofCount')}</strong>{' '}
                      {t('affiliate.hero.socialProofText')}
                    </span>
                  </p>
                </div>

                <div className="affiliate-hero__collage" aria-hidden>
                  <div className="affiliate-hero__collage-robi">
                    <img src={heroRobiImage} alt="" />
                  </div>
                  <div className="affiliate-hero__collage-phone">
                    <img src={heroPhoneImage} alt="" />
                    <div className="affiliate-stat affiliate-stat--earnings">
                      <AffiliateEarningsBadgeIcon />
                      <span className="affiliate-stat__content">
                        <span className="affiliate-stat__label">
                          {t('affiliate.hero.commissionLabel')}
                        </span>
                        <strong className="affiliate-stat__value">
                          {t('affiliate.hero.commissionValue')}
                        </strong>
                      </span>
                    </div>
                    <div className="affiliate-stat affiliate-stat--rating">
                      <AffiliateRatingBadgeIcon />
                      <span className="affiliate-stat__content">
                        <span className="affiliate-stat__label">
                          {t('affiliate.hero.ratingLabel')}
                        </span>
                        <strong className="affiliate-stat__value">
                          {t('affiliate.hero.ratingValue')}
                        </strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="affiliate-landing__section affiliate-value">
            <div className="affiliate-landing__wrapper">
              <div className="affiliate-landing__inner">
                <div className="affiliate-title affiliate-title--center">
                  <span className="affiliate-title__eyebrow">
                    {t('affiliate.platformValue.eyebrow')}
                  </span>
                  <h2>
                    {t('affiliate.platformValue.titleLead')}{' '}
                    <em>{t('affiliate.platformValue.titleHighlight')}</em>
                  </h2>
                  <p>{t('affiliate.platformValue.description')}</p>
                </div>
                <div className="affiliate-value__grid">
                  {VALUE_CARDS.map(({ key, Icon }) => (
                    <article className="affiliate-card" key={key}>
                      <span className="affiliate-card__icon">
                        <Icon />
                      </span>
                      <h5>{t(`affiliate.platformValue.${key}.title`)}</h5>
                      <p>{t(`affiliate.platformValue.${key}.description`)}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="affiliate-landing__section affiliate-benefits">
            <div className="affiliate-landing__wrapper">
              <div className="affiliate-landing__inner">
                <div className="affiliate-title affiliate-benefits__intro">
                  <span className="affiliate-title__eyebrow">
                    {t('affiliate.benefits.eyebrow')}
                  </span>
                  <h2>{t('affiliate.benefits.title')}</h2>
                  <p>{t('affiliate.benefits.description')}</p>
                </div>
                <div className="affiliate-benefits__content">
                  <div className="affiliate-benefits__grid">
                    {BENEFIT_CARDS.map(({ key, Icon }) => (
                      <article className="affiliate-card" key={key}>
                        <span className="affiliate-card__icon">
                          <Icon />
                        </span>
                        <h5>{t(`affiliate.benefits.${key}.title`)}</h5>
                        <p>{t(`affiliate.benefits.${key}.description`)}</p>
                      </article>
                    ))}
                  </div>
                  <div className="affiliate-benefits__actions">
                    <Link
                      className="affiliate-btn affiliate-btn--primary"
                      to={AFFILIATE_ROUTES.SIGN_UP}
                    >
                      {t('affiliate.nav.join')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="affiliate-landing__section affiliate-process">
            <div className="affiliate-landing__wrapper">
              <div className="affiliate-landing__inner">
                <div className="affiliate-title">
                  <span className="affiliate-title__eyebrow">
                    {t('affiliate.process.eyebrow')}
                  </span>
                  <h2>{t('affiliate.process.title')}</h2>
                </div>
                <ol className="affiliate-process__steps">
                  {PROCESS_STEPS.map(({ key, image }, index) => (
                    <li className="affiliate-step" key={key}>
                      <span className="affiliate-step__number">
                        {t('affiliate.process.stepLabel', {
                          number: String(index + 1).padStart(2, '0'),
                        })}
                      </span>
                      <span className="affiliate-step__image">
                        <img src={image} alt="" aria-hidden />
                      </span>
                      <span className="affiliate-step__body">
                        <h5>{t(`affiliate.process.${key}.title`)}</h5>
                        <p>{t(`affiliate.process.${key}.description`)}</p>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>

          <section className="affiliate-landing__section affiliate-robi">
            <div className="affiliate-landing__wrapper">
              <div className="affiliate-landing__inner">
                <div className="affiliate-robi__figure">
                  <div className="affiliate-robi__quote">
                    <p>{t('affiliate.aiBuddy.quote')}</p>
                    <span>{t('affiliate.aiBuddy.quoteAuthor')}</span>
                  </div>
                  <img src={robiImage} alt="" aria-hidden />
                </div>
                <div className="affiliate-title affiliate-robi__content">
                  <span className="affiliate-title__eyebrow">
                    {t('affiliate.aiBuddy.eyebrow')}
                  </span>
                  <h2>
                    {t('affiliate.aiBuddy.titleLead')}{' '}
                    <em>{t('affiliate.aiBuddy.titleHighlight')}</em>
                  </h2>
                  <p>{t('affiliate.aiBuddy.description')}</p>
                  <ul className="affiliate-robi__list">
                    {robiBullets.map(bullet => (
                      <li key={bullet}>
                        <AffiliateCheckBulletIcon />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="affiliate-landing__section affiliate-cta">
            <div className="affiliate-landing__wrapper">
              <div className="affiliate-landing__inner">
                <div className="affiliate-cta__panel">
                  <div>
                    <span className="affiliate-cta__eyebrow">
                      {t('affiliate.cta.eyebrow')}
                    </span>
                    <h2>
                      {t('affiliate.cta.titleFirstLine')}
                      <br />
                      {t('affiliate.cta.titleSecondLine')}
                    </h2>
                  </div>
                  <p>{t('affiliate.cta.description')}</p>
                  <div className="affiliate-cta__actions">
                    <Link className="affiliate-btn" to={AFFILIATE_ROUTES.SIGN_UP}>
                      {t('affiliate.nav.join')}
                    </Link>
                    <Link
                      className="affiliate-btn affiliate-btn--primary"
                      to={AFFILIATE_ROUTES.SIGN_IN}
                    >
                      {t('affiliate.nav.loginPrompt')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="affiliate-landing__footer">
          <div className="affiliate-landing__wrapper">
            <div className="affiliate-landing__footer-inner">
              <Link className="affiliate-landing__brand" to="/">
                <BrandLogo />
              </Link>
              <p>
                {t('affiliate.footer.copyright', {
                  year: new Date().getFullYear(),
                })}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
